import { useEffect, useState } from 'react'
import { api } from 'api'
import { AuthScheme } from '@global/api/types'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { useAuthHintQuery } from '@global/hooks/queries/useAuthHintQuery'
import { LoadingPage } from '@global/loading'

interface Auth0GuardProps {
  children: React.ReactNode
}

const Auth0AccessTokenInterceptor = ({ children }: Auth0GuardProps) => {
  const { isAuthenticated, getAccessTokenSilently, isLoading, loginWithRedirect } = useAuth0()
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!isAuthenticated) {
      if (!initialized) {
        setInitialized(true)
      }

      return
    }

    const tokenInterceptor = api.interceptors.request.use(async config => {
      const token = await getAccessTokenSilently()

      if (token) {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        config.headers.Authorization = `Bearer ${token}`
      }

      return config
    })

    if (!initialized) {
      setInitialized(true)
    }

    return () => {
      api.interceptors.request.eject(tokenInterceptor)
    }
    // initialized and setInitialized were not added as
    // dependencies to prevent re running the effect and
    // ejecting the interceptor
  }, [isLoading, isAuthenticated, getAccessTokenSilently])

  useEffect(() => {
    if (!initialized || isAuthenticated) {
      return
    }

    void loginWithRedirect()
  }, [initialized, isAuthenticated, loginWithRedirect])

  if (!initialized || !isAuthenticated) {
    return <LoadingPage />
  }

  return children
}

export const Auth0Guard = ({ children }: Auth0GuardProps) => {
  const { data: authHint, isInitialLoading } = useAuthHintQuery()

  if (isInitialLoading) {
    return <LoadingPage />
  }

  if (authHint?.auth_scheme !== AuthScheme.dsc_universal_login || !authHint.auth0) {
    return children
  }

  const { clientId, domain, orgId, audience } = authHint.auth0

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        organization: orgId,
        audience,
      }}
    >
      <Auth0AccessTokenInterceptor>{children}</Auth0AccessTokenInterceptor>
    </Auth0Provider>
  )
}
